<template>
  <div class="page__form general-form pt-6">
    <validation-observer
      ref="observer"
      v-slot="observer"
      tag="form">
      <div class="general-form__group form-group mt-4">
        <label
          class="col-form-label">ノート名</label>
        <validation-provider 
          v-slot="v"
          name="目標名"
          rules="required|max:40">
          <input
            v-model="goal.name"
            type="text"
            name="目標名"
            data-testid="goal-name-input"
            class="form-control">
          <p class="text-primary">
            {{ v.errors[0] }}
          </p>
        </validation-provider>
      </div>
      <div class="general-form__group form-group mt-3 mt-md-4">
        <template v-if="isRepeat">
          <label class="col-form-label">開始月</label>
          <b-row
            align-v="center"
            class="mx-0 px-0 col col-6">
            <year-month-select
              v-model="selectedYearMonth"
              :disabled="isEdit" />
          </b-row>
        </template>
        <template v-else>
          <label
            class="col-form-label">期間</label>
          <b-row
            align-v="center"
            align-h="center"
            class="mx-0 px-0 col">
            <b-col
              cols="12"
              sm="4"
              md="5"
              class="px-0">
              <validation-provider
                v-if="isMd"
                v-slot="v"
                rules=""
                name="開始日">
                <b-form-datepicker
                  v-model="goal.startDate"
                  placeholder="日付を選択してください"
                  hide-header>
                  <template v-slot:button-content>
                    <div
                      class="material-icons mr-2 d-flex align-items-center">
                      event_available
                    </div>
                  </template>
                </b-form-datepicker>
              </validation-provider>
              <validation-provider
                v-else
                rules="required|before_date:@終了日|valid_date"
                name="開始日">
                <b-form-input
                  v-model="goal.startDate"
                  placeholder="日付を選択してください"
                  size="sm"
                  type="date" />
              </validation-provider>
            </b-col>
            <div
              class="d-flex align-items-center mx-2">
              〜
            </div>
            <b-col
              cols="12"
              sm="4"
              md="5"
              class="px-0">
              <validation-provider
                v-if="isMd"
                name="終了日"
                rules="required|after_date:@開始日|valid_date">
                <b-form-datepicker
                  v-model="goal.endDate"
                  calendar-width="50%"
                  size="sm"
                  placeholder="日付を選択してください"
                  hide-header>
                  <template v-slot:button-content>
                    <div
                      class="material-icons mr-2 d-flex align-items-center">
                      event_available
                    </div>
                  </template>
                </b-form-datepicker>
              </validation-provider>
              <validation-provider
                v-else
                name="終了日"
                rules="required|after_date:@開始日|valid_date">
                <b-form-input
                  v-model="goal.endDate"
                  placeholder="日付を選択してください"
                  size="sm"
                  type="date" />
              </validation-provider>
            </b-col>
          </b-row>
        </template>
        <p
          class="text-primary d-flex justify-content-center">
          {{ dateErrorMessage(observer['errors']['開始日']) }}
        </p>
        <p
          class="text-primary d-flex justify-content-center">
          {{ dateErrorMessage(observer['errors']['終了日']) }}
        </p>
        <p class="text-primary d-flex justify-content-center">
          {{ nonFieldError }}
        </p>
      </div>
      <div
        v-if="!isEdit || hasRepeatGroup"
        class="general-form__group form-group mt-3 mt-md-4">
        <label
          class="col-form-label">毎月繰り返す</label>
        <div>
          <validation-provider
            v-slot="v"
            name="毎月繰り返す"
            rules="required">
            <label
              class="repeat-btn">
              <img
                v-if="isRepeat"
                class="repeat-btn__icon"
                src="@/assets/images/icon_repeat_active.svg">
              <img
                v-else
                class="repeat-btn__icon"
                src="@/assets/images/icon_repeat.svg">
              <input
                style="display: none;"
                :value="isRepeat"
                type="checkbox"
                @input="onClickRepeat">
              <p class="text-primary">
                {{ v.errors[0] }}
              </p>
            </label>
          </validation-provider>
          <p class="fs-m">
            練習ノートなど記録数の多いノートには<img
              src="@/assets/images/icon_repeat.svg"
              class="mx-1 mb-1"
              style="width: 16px; height: 16px;">を押します<br>
            ※ノートが月ごとに分かれ振り返りやすくなります
          </p>
        </div>
      </div>
      <div class="general-form__group form-group mt-3 mt-md-4">
        <label
          class="col-form-label">
          記録目標数
          <p>※ノートを記録する目標日数を入れてください</p>
        </label>
        <validation-provider
          v-slot="v"
          name="記録目標数"
          rules="required|hankaku_number">
          <input
            v-model="goal.goalNumber"
            type="number"
            min="0"
            data-testid="goal-number-input"
            class="form-control goal-number-input">
          <p
            class="text-primary">
            {{ v.errors[0] }}
          </p>
        </validation-provider>
      </div>
      <div class="general-form__group form-group mt-3 mt-md-4">
        <label
          class="col-form-label">上部に固定</label>
        <validation-provider
          v-slot="v"
          name="上部に固定"
          rules="required">
          <label
            class="goal-pin-btn">
            <img
              v-if="goal.pin"
              class="goal-pin-btn__icon"
              src="@/assets/images/icon_pin.svg">
            <img
              v-else
              class="goal-pin-btn__icon"
              src="@/assets/images/icon_pin-g.svg">
            <input
              style="display: none;"
              :value="goal.pin"
              type="checkbox"
              @input="onClickPin">
            <p class="text-primary">
              {{ v.errors[0] }}
            </p>
          </label>
        </validation-provider>
      </div>
      <div
        v-if="isLockGoal"
        class="general-form__group form-group mt-3 mt-md-4">
        <label
          class="col-form-label">限定公開</label>
        <validation-provider
          v-slot="v"
          name="限定公開"
          rules="required">
          <label class="goal-lock-btn">
            <span
              class="material-icons goal-lock-btn__icon"
              :class="{'goal-lock-btn__icon--locked': goal.isLocked}">
              lock
            </span>
            <input
              style="display: none;"
              :value="goal.isLocked"
              type="checkbox"
              @input="onClickLockBtn">
            <p class="text-primary">
              {{ v.errors[0] }}
            </p>
          </label>
        </validation-provider>
      </div>
      <div
        v-if="isShowConditionsAndBodyPain"
        class="general-form__group form-group mt-3 mt-md-4">
        <div>
          <label
            class="col-form-label">
            コンディション項目を追加
            <p>
              ※１つのノートに最大８つの項目をセットできます
            </p>
          </label>
        </div>
        <div>
          <validation-provider
            v-slot="v"
            name="コンディション項目"
            rules="required">
            <label class="toggle-svg-button">
              <input
                style="display: none;"
                :value="isConditionAccordionOpen"
                type="checkbox"
                @input="toggleConditionAccordion">
              <img
                class="toggle-svg-button__icon"
                :class="{active: isConditionAccordionOpen}"
                src="@/assets/images/icon_condition.svg">
              <p class="text-primary">
                {{ v.errors[0] }}
              </p>
            </label>
          </validation-provider>
          <b-collapse
            id="conditions"
            v-model="isConditionAccordionOpen">
            <div
              v-for="conditions in conditionOptions"
              :key="conditions.title"
              class="condition-column">
              <label class="condition-column__label">{{ conditions.title }}</label>
              <template
                v-for="condition in conditions.items">
                <validation-provider
                  :key="condition.value"
                  v-slot="v"
                  class="d-flex align-items-center condition"
                  :name="condition.fullLabel">
                  <label class="d-flex align-items-center mb-0">
                    <input
                      v-model="goal.conditionItems"
                      type="checkbox"
                      :value="condition.value"
                      class="checkbox"
                      :disabled="isConditionCheckboxDisable(condition.value)"
                      @click="onClickConditionCheckbox(condition.value)">
                    <span class="label">{{ condition.fullLabel }}</span>
                  </label>
                  <span class="unit">{{ condition.unit }}</span>
                </validation-provider>
              </template>
            </div>
          </b-collapse>
        </div>
      </div>
      <div
        v-if="isShowConditionsAndBodyPain"
        class="general-form__group form-group mt-3 mt-md-4">
        <label
          class="col-form-label">
          体の痛みを記録する
          <p>※オンにすると体の痛みを記録できます</p>
        </label>
        <div>
          <validation-provider
            v-slot="v"
            name="体の痛みを記録する"
            rules="required">
            <label class="body-pain-button toggle-svg-button">
              <input
                style="display: none;"
                :value="goal.isRecordBodyPain"
                type="checkbox"
                @input="onClickRecordBodyPainButton">
              <img
                class="toggle-svg-button__icon"
                :class="{active: goal.isRecordBodyPain}"
                src="@/assets/images/icon_body_pain.svg">
              <span
                v-if="goal.isRecordBodyPain"
                class="text">
                記録する
              </span>
            </label>
            <p class="text-primary">
              {{ v.errors[0] }}
            </p>
          </validation-provider>
        </div>
      </div>
      <div class="general-form__btn row mt-6">
        <div class="col-6">
          <button
            type="button"
            class="btn btn-block btn-cancel"
            @click="onClickCancel">
            キャンセル
          </button>
        </div>
        <div class="col-6">
          <button
            type="button"
            class="btn btn-block btn-primary"
            @click="customPasses(onClickSave)">
            保存
          </button>
        </div>
      </div>
      <div
        v-if="isEdit"
        class="general-form__item d-flex form-info justify-content-between mt-6">
        <div
          style="cursor: pointer;"
          class="form-info__delete"
          @click="onClickDelete">
          <span class="form-info__icon material-icons">
            delete
          </span>
          削除する
        </div>
        <div class="form-info__detail text-right">
          <div class="form-info__name">
            <p><b class="mr-2">作成者</b>{{ goal.createdUserName }}</p>
          </div>
          <div class="form-info__date">
            <p>最終更新日時: {{ goal.updatedAt | prettyDateTime }}</p>
          </div>
        </div>
      </div>
    </validation-observer>
  </div>
</template>

<script>
import YearMonthSelect from '@/components/parts/YearMonthSelect.vue'
import format from 'date-fns/format'
import startOfMonth from 'date-fns/startOfMonth'
import endOfMonth from 'date-fns/endOfMonth'
import validate from '@/components/mixin/validate'
import { conditionOptions } from '@/helper/conditions'

const MAX_CONDITIONS_COUNT = 8

export default {
  components: {
    YearMonthSelect
  },
  mixins: [validate],
  props: {
    /**
     * goal: {
     *   name: string,
     *   startDate: string,
     *   endDate: string,
     *   goalNumber: number,
     *   pin: boolean,
     *   isLocked: boolean,
     *   conditionItems: string[],
     *   isRecordBodyPain: boolean
     * }
     */
    goalObject: {
      type: Object,
      default: null
    },
    isLockGoal: {
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    nonFieldError: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      goal: {
        name: '',
        startDate: '',
        endDate: '',
        goalNumber: null,
        pin: false,
        isLocked: false,
        conditionItems: [],
        isRecordBodyPain: false
      },
      isMd: false,
      isRepeat: false,
      selectedYearMonth: null,
      isConditionAccordionOpen: false,
    }
  },
  computed: {
    memberId() {
      return this.$route.params.id
    },
    hasRepeatGroup() {
      return this.goalObject.repeatGroup != null
    },
    isConditionOn() {
      return this.goal.conditionItems.length > 0
    },
    conditionOptions() {
      return conditionOptions
    },
    isShowConditionsAndBodyPain() {
      if (this.hasRepeatGroup || !this.isEdit) return true
      return this.goalObject?.conditionItems.length > 0 || this.goalObject?.isRecordBodyPain
    },
  },
  watch: {
    isRepeat() {
      const date = this.goal.startDate
      this.setGoalRange(date)
    },
    selectedYearMonth(date) {
      if (this.isRepeat){
        this.setGoalRange(date)
      }
    },
  },
  created() {
    this.goal = Object.assign({}, this.goalObject)
    this.isRepeat = this.hasRepeatGroup
    this.selectedYearMonth = format(new Date(this.goal.startDate), 'yyyy-MM')
    this.isConditionAccordionOpen = this.isConditionOn
  },
  methods: {
    setGoalRange(date) {
      const baseDate = new Date(date)
      const startDate = startOfMonth(baseDate)
      const endDate = this.isRepeat ? endOfMonth(baseDate) : new Date(2099, 11, 31)
      this.goal = {...this.goal, startDate: format(startDate, 'yyyy-MM-dd'), endDate: format(endDate, 'yyyy-MM-dd')}
    },
    async onClickSave() {
      if (this.isEdit) {
        const isChangedRange = this.goal.startDate !== this.goalObject.startDate 
          || this.goal.endDate !== this.goalObject.endDate
        const patchBody = {
          name: this.goal.name,
          start_date: isChangedRange ? this.goal.startDate : undefined,
          end_date: isChangedRange ? this.goal.endDate : undefined,
          goal_number: this.goal.goalNumber,
          pin: this.goal.pin,
          is_locked: this.goal.isLocked,
          condition_setting: {
            items: this.isConditionAccordionOpen ? this.goal.conditionItems : []
          },
          body_pain_setting: {
            is_record_body_pain: this.goal.isRecordBodyPain
          }
        }

        const willRepeatOff = this.isRepeat !== this.hasRepeatGroup && !this.isRepeat
        
        this.$emit('onClickSave', patchBody, willRepeatOff)
      } else {
        const requestBody = {
          name: this.goal.name,
          start_date: this.goal.startDate,
          end_date: this.goal.endDate,
          goal_number: this.goal.goalNumber,
          pin: this.goal.pin,
          is_locked: this.goal.isLocked,
          condition_setting: {
            items: this.isConditionAccordionOpen ? this.goal.conditionItems : []
          },
          body_pain_setting: {
            is_record_body_pain: this.goal.isRecordBodyPain
          }
        }
        this.$emit('onClickSave', requestBody, this.isRepeat)
      }
    },
    dateErrorMessage(v) {
      if (!v) return ''
      return v[0]
    },
    onClickCancel() {
      this.$router.push(`/members/${this.memberId}`)
    },
    onClickPin() {
      this.goal.pin = !this.goal.pin
    },
    onClickLockBtn() {
      if (this.goal.isLocked && window.confirm('すべてのメンバーに公開しますか？')) {
        this.goal.isLocked = false
        return
      }
      if (window.confirm('自分と指導者だけに限定して公開しますか？')) {
        this.goal.isLocked = true
      } 
    },
    onClickRepeat() {
      // コンディション項目がONの場合は毎月繰り返すがOFFにできない
      if (this.isConditionAccordionOpen) {
        this.$store.commit('notify/set', {message: 'コンディション項目がオンの場合は、毎月繰り返すをオフにできません'})
        return
      }
      this.isRepeat = !this.isRepeat
    },
    onClickConditionCheckbox(value) {
      if (this.goal.conditionItems.includes(value)) {
        this.goal.conditionItems = this.goal.conditionItems.filter(item => item !== value)
      } else {
        this.goal.conditionItems.push(value)
      }
    },
    isConditionCheckboxDisable(value) {
      return this.goal.conditionItems.length >= MAX_CONDITIONS_COUNT && !this.goal.conditionItems.includes(value)
    },
    onClickRecordBodyPainButton() {
      this.goal.isRecordBodyPain = !this.goal.isRecordBodyPain
    },
    onClickDelete() {
      this.$emit('onClickDelete', this.hasRepeatGroup)
    },
    toggleConditionAccordion() {
      const newVal = !this.isConditionAccordionOpen
      this.isConditionAccordionOpen = newVal
      // コンディション項目がONの場合は毎月繰り返すが自動的にONになる
      if (newVal) {
        this.isRepeat = true
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.col-form-label {
  p {
    font-weight: normal;
    color: $grey04;
    font-size: $fs-m;
  }
}

.goal-number-input {
  width: 50%;
}

.goal-lock-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  padding: 3px;
  background-color: $white;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  &__icon {
    color: $grey11;
    &--locked {
      color: $black;
    }
  }
}

.repeat-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background-color: $white;
  padding: 3px;
  text-align: center;
  &__icon {
    cursor: pointer;
    width: 20px;
    height: auto;
  }
}

.goal-pin-btn {
  display: block;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background-color: $white;
  padding: 3px;
  text-align: center;
  &__icon {
    width: 20px;
    height: auto;
  }
}

.edit-head{
  background-color: $l-blue;
  @media screen and (min-width:992px) {
    width: calc(100% - 422px);
    right: 0;
    left: auto;
  }
  &__icon{
    height: 22px;
    width: auto;
  }
  &__heading{
    color: $white;
    font-size: $fs-lg;
    font-weight: bold;
  }
}

.toggle-svg-button {
  background: $white;
  border: none;
  border-radius: 5px;
  padding: 5px;

  img {
    width: 30px;
    height: 30px;
    filter: invert(82%) sepia(0%) saturate(0%) hue-rotate(272deg) brightness(81%) contrast(89%);
  }

  &__icon.active {
    filter: none;
  }
}

.body-pain-button {
  display: inline-flex;
  align-items: center;
  padding: 5px;
  background-color: $white;
  
  &.active {
    width: auto;
  }
  .text {
    color: $grey17;
    font-weight: bold;
    margin-left: 10px;
    margin-right: 5px;
  }
  img {
    width: 30px;
    height: auto;
  }
}

#conditions {
  border-top: dashed 1px $grey17;
  margin-top: 20px;
}

.condition-column {
  border-bottom: dashed 1px $grey17;
  padding-top: 12px;
  &__label {
    font-weight: 600;
  }
}

.condition {
  margin-bottom: 12px;

  .checkbox {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    background: white;
    border-radius: 5px;
    margin: 0 12px 0 8px;
    position: relative;
    transition: all 0.1s ease;

    &:checked::after {
      border-right: 3px solid $grey17;
      border-bottom: 3px solid $grey17;
      content: '';
      display: block;
      height: 15px;
      margin-top: -7px;
      position: absolute;
      top: 38%;
      left: 23%;
      transform: rotate(39deg);
      width: 11px;
    }

    &:disabled {
      background: $grey11;
    }
  }

  .label {
    font-weight: 600;
    width: 150px;
  }

  .unit {
    font-size: $fs-m;
  }
}

.form-info{
  color: $grey09;
  &__delete{
    text-decoration: none;
    font-weight: bold;
    color: $grey09;
  }
  &__icon{
    display: inline-block;
    vertical-align: top;
  }
  &__date{
    font-size: $fs-m;
  }
  &__detail {
    flex: 1;
  }
}
</style>